import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ImagePlus, Send, Trash2, ArrowLeft, Info, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const MAX_PHOTOS = 5;

export default function TutorialNew() {
  const navigate = useNavigate();
  const { id: editId } = useParams();  // reused for edit mode via same page /tutos/edit/:id
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    title: "",
    summary: "",
    body_markdown: "",
    category: "",
    spa_brand: "",
    tags: "",
    video_url: "",
  });
  const [tutorial, setTutorial] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    api.get("/tutorials/categories").then((r) => setCategories(r.data || []));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.body_markdown.trim().length < 30) {
      toast.error("Le contenu doit contenir au moins 30 caractères.");
      return;
    }
    setSubmitting(true);
    const payload = {
      title: form.title.trim(),
      summary: form.summary.trim(),
      body_markdown: form.body_markdown.trim(),
      category: form.category,
      spa_brand: form.spa_brand.trim() || null,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      video_url: form.video_url.trim() || null,
    };
    try {
      const r = await api.post("/tutorials", payload);
      setTutorial(r.data);
      toast.success("Tuto créé — ajoutez des photos puis soumettez-le à validation.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  const onUploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !tutorial) return;
    setUploading(true);
    for (const file of files) {
      if ((tutorial.photos || []).length >= MAX_PHOTOS) {
        toast.error(`Maximum ${MAX_PHOTOS} photos par tuto`);
        break;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post(`/tutorials/${tutorial.id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setTutorial(r.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Upload ${file.name} échoué`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDone = () => {
    toast.success("Votre tuto est en attente de modération. Vous serez notifié dès sa publication.");
    navigate("/tutos/mes-tutos");
  };

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-10 py-12 lg:py-16" data-testid="tutorial-new-page">
      <Link to="/tutos" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" /> Retour aux tutos
      </Link>

      <div className="eyebrow text-secondary">Contribuez</div>
      <h1 className="mt-2 font-display text-3xl lg:text-4xl font-black tracking-tight">
        Proposer un tuto
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Partagez votre expérience avec la communauté. Tous les tutos sont validés par un modérateur avant publication.
      </p>

      {!tutorial ? (
        <form onSubmit={onSubmit} className="mt-8 space-y-6" data-testid="tuto-create-form">
          <div className="space-y-2">
            <Label htmlFor="title">Titre du tuto *</Label>
            <Input
              id="title"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Ex. Remplacer un pressostat sur un Balboa GS100"
              required
              minLength={6}
              maxLength={200}
              data-testid="tuto-input-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="summary">Résumé (accroche courte) *</Label>
            <Input
              id="summary"
              value={form.summary}
              onChange={(e) => set("summary", e.target.value)}
              placeholder="1-2 phrases qui résument ce que le lecteur va apprendre."
              maxLength={400}
              data-testid="tuto-input-summary"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Catégorie *</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="tuto-select-category">
                  <SelectValue placeholder="Choisir…" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.slug} value={c.slug} data-testid={`tuto-category-opt-${c.slug}`}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="brand">Marque du spa (optionnel)</Label>
              <Input
                id="brand"
                value={form.spa_brand}
                onChange={(e) => set("spa_brand", e.target.value)}
                placeholder="Ex. Balboa, Gecko, Jacuzzi…"
                maxLength={120}
                data-testid="tuto-input-brand"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags (séparés par des virgules)</Label>
            <Input
              id="tags"
              value={form.tags}
              onChange={(e) => set("tags", e.target.value)}
              placeholder="pressostat, gs100, plomberie"
              data-testid="tuto-input-tags"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="video">Vidéo YouTube / Vimeo (optionnel)</Label>
            <Input
              id="video"
              value={form.video_url}
              onChange={(e) => set("video_url", e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
              maxLength={500}
              type="url"
              data-testid="tuto-input-video"
            />
            <p className="text-xs text-muted-foreground">Seules les URLs YouTube et Vimeo sont acceptées.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Contenu du tuto (Markdown) *</Label>
            <Textarea
              id="body"
              value={form.body_markdown}
              onChange={(e) => set("body_markdown", e.target.value)}
              placeholder={"# Matériel\n- Tournevis, pinces…\n\n# Étape 1 — Couper l'alimentation\nDétails…"}
              rows={14}
              maxLength={30000}
              className="font-mono text-sm"
              required
              data-testid="tuto-textarea-body"
            />
            <p className="text-xs text-muted-foreground">
              Vous pouvez utiliser du <strong>Markdown</strong> : #, ##, listes, **gras**, [liens](url). 30 caractères minimum.
            </p>
          </div>

          <Card className="p-4 bg-blue-50 border-blue-200 flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-xs text-blue-900">
              <strong>Modération :</strong> votre tuto sera revu par un modérateur DoctoSpa avant publication (généralement sous 48 h). Vous serez notifié dès sa mise en ligne.
            </div>
          </Card>

          <Button type="submit" size="lg" className="rounded-full h-12 px-8" disabled={submitting || !form.title || !form.category || !form.body_markdown} data-testid="tuto-submit-create">
            {submitting ? "Création…" : (<><Send className="h-4 w-4 mr-2" /> Créer le brouillon</>)}
          </Button>
        </form>
      ) : (
        <div className="mt-8 space-y-6" data-testid="tuto-photos-step">
          <Card className="p-5 bg-emerald-50 border-emerald-200">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-emerald-900">Brouillon enregistré !</p>
                <p className="text-sm text-emerald-800 mt-1">
                  Ajoutez maintenant jusqu'à {MAX_PHOTOS} photos (la première sert de couverture) puis validez pour envoyer à modération.
                </p>
              </div>
            </div>
          </Card>

          <div className="space-y-3">
            <Label>Photos ({(tutorial.photos || []).length}/{MAX_PHOTOS})</Label>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {(tutorial.photos || []).map((p, i) => (
                <div key={p.path} className="aspect-square rounded-lg overflow-hidden bg-slate-100 relative group" data-testid={`tuto-photo-preview-${i}`}>
                  <img src={`${backend}${p.url}`} alt={p.filename} className="h-full w-full object-cover" />
                  {i === 0 && (
                    <Badge className="absolute top-1 left-1 text-[9px] bg-primary">Couverture</Badge>
                  )}
                </div>
              ))}
              {(tutorial.photos || []).length < MAX_PHOTOS && (
                <label className="aspect-square rounded-lg border-2 border-dashed border-black/20 hover:border-primary/50 grid place-items-center cursor-pointer transition-colors" data-testid="tuto-upload-photo-btn">
                  <div className="text-center text-xs text-muted-foreground">
                    <ImagePlus className="h-6 w-6 mx-auto mb-1" />
                    {uploading ? "Envoi…" : "Ajouter"}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={onUploadPhoto}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-muted-foreground">JPG/PNG/WebP, 5 Mo max par photo.</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button onClick={onDone} size="lg" className="rounded-full h-12 px-6" data-testid="tuto-submit-for-review">
              <Send className="h-4 w-4 mr-2" /> Envoyer à modération
            </Button>
            <Link to="/tutos/mes-tutos">
              <Button variant="outline" size="lg" className="rounded-full h-12 px-6" data-testid="tuto-goto-mine">
                Voir mes tutos
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
